import { useProfile } from '@shared/hooks/useProfile';
import { collection, doc, onSnapshot, query, setDoc, where } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../../../config/firebase';
import { Conversation } from '../types';
import { useConnections } from './useConnections';
import { useLastMessages } from './useLastMessages';

export const useConversationsData = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useProfile();

  // Get connections data from cache
  const { data: connectionsData } = useConnections();

  // Get last messages and unread counts
  const lastMessages = useLastMessages();

  // Subscribe to conversations
  useEffect(() => {
    if (!user?.uid) return;

    const q = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', user.uid)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const newConversations = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Conversation[];

      setConversations(newConversations);
      setLoading(false);
    }, (err) => {
      setError(err.message);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user?.uid]);

  const getOrCreateConversation = async (otherUserId: string): Promise<string | null> => {
    if (!user?.uid) return null;

    // First, check if a direct conversation already exists
    const existingConversation = conversations.find(
      (conv) =>
        conv.type === "direct" &&
        conv.participants.length === 2 &&
        conv.participants.includes(otherUserId) &&
        conv.participants.includes(user.uid)
    );

    if (existingConversation) {
      return existingConversation.id;
    }

    // If it doesn't exist, create a new conversation
    try {
      // Create a new document reference
      const newDocRef = doc(collection(db, 'conversations'));

      // Set the document data
      await setDoc(newDocRef, {
        participants: [user.uid, otherUserId],
        createdAt: new Date(),
        updatedAt: new Date(),
        deleted: false,
        unreadCount: 0,
        type: "direct"
      });

      return newDocRef.id;
    } catch (err: any) {
      setError(err.message);
      return null;
    }
  };

  // Enhance conversations with last message data
  const enhancedConversations = conversations.map(conv => ({
    ...conv,
    lastMessage: lastMessages[conv.id]?.lastMessage || null,
    unreadCount: lastMessages[conv.id]?.unreadCount || 0
  }));

  return {
    conversations: enhancedConversations,
    loading,
    error,
    connectedUsers: connectionsData?.connectedUsers || [],
    getOrCreateConversation,
    userProfiles: connectionsData?.userProfiles || {}
  };
};
